import { Panel } from "../panel/Panel";
import { getLogger, getMapSettings, getUnauthSettings } from "@mcleod/core";
import { VendorMap, Map } from "./Map";
import { MapPin } from "./MapPin";
import MarkerClusterer from "@googlemaps/markerclustererplus";
import { MapVendor, VendorMapProps } from "./MapProps";
import { getSVGUrl } from "@mcleod/images";

const log = getLogger("components/Map");

interface google {
  maps: typeof window.google.maps,
}

export enum RouteType {
  Google,
  Trimble
}

export class GoogleMap extends Panel implements VendorMap {
  map: google.maps.Map;
  markerClusterer: MarkerClusterer;
  markers: google.maps.Marker[];
  parentMap: Map;
  directionsService: google.maps.DirectionsService;
  directionsRenderer: google.maps.DirectionsRenderer;
  trimblePath: google.maps.Polyline;

  constructor(props?: Partial<VendorMapProps>) {
    super({ borderRadius: 4, ...props });
    if (props.parentMap == null)
      throw new Error("GoogleMap needs to have its parentMap property set.");
    this.markers = [];
    this.parentMap.vendor = MapVendor.GOOGLE;
  }

  async init() {
    if (window.google == null)
      await this.initHeader();
    else
      this.initMap();
  }

  addPin(pin: MapPin): void {
    if (this.map == null)
      return;
    const image = pin.image == null ? undefined : {icon: {
      url: getSVGUrl(pin.image),
      labelOrigin: new google.maps.Point(12, 12)
      }
    };
    const marker = new window.google.maps.Marker({
      ...image,
      position: new window.google.maps.LatLng(pin.latitude, pin.longitude),
      label: {
        text: pin.caption,
        color: pin.captionColor,
        fontSize: "12px"
      },
      title: pin.tooltip
    });

    this.markers.push(marker);
    if (this.parentMap.pinNeedsClickListener(pin))
      marker.addListener("click", (event) => this.parentMap.pinClicked(pin, event.domEvent));
    if (pin.clusterable === true)
      this.markerClusterer.addMarker(marker);
    else
      marker.setMap(this.map);
  }

  removeAllPins() {
    this.markerClusterer?.removeMarkers(this.markers);
    for (const marker of this.markers)
      marker.setMap(null);
    this.markers = [];
  }

  private initHeader(): Promise<void> {
    const url = getUnauthSettings().company_settings.google_maps_url;
    const apiKey = getUnauthSettings().company_settings.google_api_key;
    const script = document.createElement("script");
    script.src = url + apiKey;
    window.document.body.appendChild(script);
    return new Promise((resolve, reject) => {
      script.onload = () => {
        this.initMap();
        log.debug("Map initialized successfully");
        resolve();
      };
      script.onerror = () => {
        log.debug("An error occurred during map initialization");
        reject();
      };
    });
  }

  private getGestureHandling() {
    if(getMapSettings()?.google_greedy)
      return "greedy";
    else
      return "cooperative";
  }

  private initMap() {
    this.map = new window.google.maps.Map(this._element, {
      disableDefaultUI: false,
      gestureHandling: this.getGestureHandling(),
      center: new window.google.maps.LatLng(39.8283, -98.5795),
      zoom: 5
    });
    this.markerClusterer = new MarkerClusterer(this.map, [], {
      styles: [
        {
          textColor: "white",
          anchorText: [18, 0],
          fontWeight: "600",
          url: getSVGUrl("map-pin-cluster"),
          height: 48,
          width: 48
        }
      ],
      maxZoom: 12,
      gridSize: 20
    });
    this.directionsService = new google.maps.DirectionsService();
    this.directionsRenderer = new google.maps.DirectionsRenderer();
  }

  fitPins() {
    if (this.map == null)
      return;
    if (this.markers.length === 1)
      this.map.setCenter(this.markers[0].getPosition());
    else if (this.markers.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      for (const marker of this.markers)
        bounds.extend(marker.getPosition());
      this.map.fitBounds(bounds);
    }
    if (this.map.getZoom() > 10)
      this.map.setZoom(10);
  }

  setZoom(level: number) {
    this.map.setZoom(level);
  }

  createRoute(routeData: any[], routeType: RouteType) {
    if (routeType == RouteType.Google) {
      const data: google.maps.LatLngLiteral[] = this.formatGoogleRouteData(routeData);
      this.directionsService
        .route({
          origin: data[0]["location"],
          destination: data[data.length - 1]["location"],
          waypoints: data.slice(1, -1) as google.maps.DirectionsWaypoint[],
          travelMode: google.maps.TravelMode.DRIVING
        })
        .then((response) => {
          this.directionsRenderer.setDirections(response);
          this.directionsRenderer.setMap(this.map);
        });
    }
    if (routeType == RouteType.Trimble) {
      if (this.trimblePath) {
        this.trimblePath.setMap(null);
      }
      this.trimblePath = new google.maps.Polyline({
        path: routeData,
        geodesic: true,
        strokeColor: "#FF0000",
        strokeOpacity: 1.0,
        strokeWeight: 4,
      });
      this.trimblePath.setMap(this.map);
    }
  }

  clearRoute() {
    this.directionsRenderer.setMap(null);
    if (this.trimblePath) {
      this.trimblePath.setMap(null);
    }
  }

  private formatGoogleRouteData(routeData: any[]): google.maps.LatLngLiteral[] {
    const coordinatesList = [];
    routeData.forEach(stop => {
      const lat = Number(stop.latitude);
      const lng = Number(Math.abs(stop.longitude) * -1);
      coordinatesList.push({ "location": { lat, lng } });
    });
    return coordinatesList;
  }
  // private createMarkers(pins: MapPin[], markers: google.maps.Marker[], onPinClick) {
  //   this.clearMarkers(markers);
  //   let clusters:google.maps.Marker[] = [];
  //   for (let pin of pins) {
  //     let duplicateLocations = this.findDuplicateLocation(pins, pin);
  //     if (duplicateLocations.length > 0)
  //       this.createMultiLocationPin(markers, duplicateLocations, onPinClick);
  //     else
  //       this.createSingleLocationPin(markers, clusters, pin, onPinClick);
  //   }
  //   this.markerClusterer.addMarkers(clusters);
  // }

  // private clearMarkers(markers: google.maps.Marker[]) {
  //   for (let marker of markers)
  //     marker.setMap(null);
  //   this.markerClusterer.clearMarkers();
  // }

  // private findDuplicateLocation(pins: MapPin[], pin: MapPin): MapPin[] {
  //   let duplicatePins = [];
  //   if (pin.clusterable !== false)
  //     for (let dupCheckPin of pins)
  //       if (dupCheckPin.latitude === pin.latitude)
  //         duplicatePins.push(dupCheckPin);
  //   if (duplicatePins.length > 0)
  //     duplicatePins.push(pin);
  //   duplicatePins = duplicatePins.reverse();
  //   return duplicatePins;
  // }

  // private getSVGUrl(name: string): string {
  //   return undefined;
  // }

  // private createMultiLocationPin(markers: google.maps.Marker[], duplicateLocations: MapPin[], onPinClick) {
  //   let count = duplicateLocations.length.toString();
  //   let multiMarkers = [];
  //   for (let pin of duplicateLocations) {
  //     let markerImg = {
  //       url: this.getSVGUrl("map-pin-white-dot-large"),
  //       scaledSize: new window.google.maps.Size(24, 40),
  //       labelOrigin: new window.google.maps.Point(11, 12)
  //     }
  //     let position = {
  //       lat: pin.latitude,
  //       lng: pin.longitude
  //     }
  //     let marker = new window.google.maps.Marker({
  //       icon: markerImg,
  //       label: {
  //         text: count,
  //         color: '#FF6600',
  //         fontSize: '13px',
  //         fontWeight: 'bold',
  //       },
  //       position: position,
  //       pin: pin,
  //       id: pin.pinId
  //     });
  //     multiMarkers.push(marker);
  //     marker.setMap(this.map);
  //     markers.push(marker);
  //   }
  //   this.setupMultiTooltip(duplicateLocations, multiMarkers);
  //   this.addMultiMarkerListeners(multiMarkers, onPinClick);
  // }

  // private createSingleLocationPin(markers: google.maps.Marker[], clusters: google.maps.Marker[], pin: MapPin, onPinClick) {
  //   log.debug("Create pin %o", pin);
  //   let markerImg = { url: this.getSVGUrl("map-pin-white-dot-small") };
  //   let label = this.setupCaptionedPin(pin, markerImg);
  //   if (pin.image)
  //     markerImg.url = pin.image;
  //   let marker = this.createMarker(markerImg, pin, pin.pinId, label);
  //   this.addPinClickListener(marker, onPinClick);
  //   this.setupTooltip(pin, marker);
  //   markers.push(marker);
  //   if (pin.clusterable === false)
  //     marker.setMap(this.map);
  //   else
  //     clusters.push(marker);
  // }

  // private setupCaptionedPin(pin: MapPin, markerImg) {
  //   if (pin.caption) {
  //     let fontColor = getThemeColor("primary");
  //     if (pin.captionColor)
  //       fontColor = getThemeColor(pin.captionColor);
  //     markerImg.url = this.getSVGUrl("map-pin-outlined");
  //     return {
  //       text: pin.caption,
  //       color: fontColor,
  //       fontWeight: "500"
  //     };
  //   }
  //   return null;
  // }

  // private setupTooltip(pin: MapPin, marker: google.maps.Marker) {
  //   if (pin.tooltip != null) {
  //     let infoWindow = new window.google.maps.InfoWindow({
  //       content: '<div>' + pin.tooltip + '</div>' // we'll want the ability for the caller to pass more complicated html
  //     });
  //     marker.addListener('mouseover', () => infoWindow.open(this.map, marker));
  //     marker.addListener('mouseout', () => infoWindow.close());
  //   }
  // }

  // private setupMultiTooltip(pins: MapPin[], markers: google.maps.Marker[]) {
  //   let content = "<div><div>" + pins.length + " orders at this exact location.</div>";
  //   for (let i = 0; i < 20 && i < pins.length; i++) {
  //     const pin = pins[i];
  //     if (pin.tooltip)
  //       content += "<div>" + pin.tooltip + "</div>";
  //   }
  //   const infoWindow = new window.google.maps.InfoWindow({
  //     content: content
  //   });

  //   for (let i = 0; i < 20 && i < markers.length; i++) {
  //     const marker = markers[i];
  //     marker.addListener('mouseover', () =>  infoWindow.open(this.map, marker));
  //     marker.addListener('mouseout', () => infoWindow.close());
  //   }
  // }


  // private createMarker(markerImg, pin: MapPin, pinId: string, label: any): google.maps.Marker {
  //   let position = {
  //     lat: pin.latitude,
  //     lng: pin.longitude
  //   }
  //   return new window.google.maps.Marker({
  //     icon: markerImg,
  //     position: position,
  //     id: pinId,
  //     label: label,
  //     pin: pin
  //   });
  // }

  // private addPinClickListener(marker: google.maps.Marker, onPinClick) {
  //   if (onPinClick)
  //     marker.addListener('click', () => { onPinClick([marker.pin]) });
  // }

  // private addMultiMarkerListeners(markers: google.maps.Marker[], onPinClick) {
  //   if (onPinClick) {
  //     let pins = [];
  //     for (let i = 0; i < markers.length; i++) {
  //       pins.push(markers[i].pin);
  //       markers[i].addListener('click', () => { onPinClick(pins) });
  //     }
  //   }
  // }

  // private setMaximumZoom() {
  //   if (this.map.getZoom() > 10)
  //     this.map.setZoom(10);
  // }
}
